<script>
import * as m from "@app/paraglide/messages.js";
import { showModal } from "@app/store/modalStore.svelte.js";
import {
    projectState,
    switchMediaVersion,
    removeMediaVersion,
    reorderMediaVersions,
} from "@app/store/projectStore.svelte.js";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { isRemoteUrlSource } from "@app/lib/player/mediaSources.js";

let switchingVersionId = $state(false);
let mediaVersions = $derived(projectState.mediaVersions || []);
let activeVersionId = $derived(projectState.activeMediaVersion);

async function handleSwitch(versionId) {
    if (switchingVersionId || versionId === activeVersionId) {
        return;
    }

    switchingVersionId = versionId;

    try {
        await switchMediaVersion(versionId);
    } finally {
        switchingVersionId = false;
    }
}

function handleRowKey(event, versionId) {
    if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleSwitch(versionId);
    }
}

async function handleRemove(versionId) {
    const version = mediaVersions.find((entry) => entry.versionId === versionId);
    if (!version) {
        return;
    }

    const result = await Swal.fire({
        ...SWAL_BASE,
        titleText: m.media_tab_remove_title(),
        text: m.media_tab_remove_confirm({ name: version.versionName }),
        showCancelButton: true,
        confirmButtonText: m.yes(),
        cancelButtonText: m.cancel(),
        customClass: SWAL_CLASSES.confirmCancel,
    });

    if (result.isConfirmed) {
        await removeMediaVersion(versionId);
    }
}

function moveVersion(index, direction) {
    reorderMediaVersions(index, index + direction);
}

function isStreamingStorage(storage) {
    return isRemoteUrlSource(storage);
}

function formatDuration(duration) {
    if (!duration && duration !== 0) {
        return m.media_tab_duration_unknown();
    }

    const totalSeconds = Math.floor(Number(duration));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
</script>

<div id="media-tab-content" aria-label={m.media_tab_aria_label()}>
    {#if mediaVersions.length === 0}
        <div class="media-empty text-center py-4">
            <i class="bi bi-film media-empty-icon text-muted mb-3"></i>
            <p class="text-muted mb-3">{m.media_tab_empty()}</p>
            <button
                type="button"
                class="btn btn-primary"
                onclick={() => showModal("mediaImport")}
            >
                <i class="bi bi-plus-lg me-1"></i>{m.media_tab_import_button()}
            </button>
        </div>
    {:else}
        <div class="media-header d-flex justify-content-between align-items-center mb-2 gap-2">
            <p class="text-muted mb-0 small">{m.media_tab_desc()}</p>
            <div class="d-flex gap-2">
                <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary text-nowrap"
                    title={m.audio_stems_title()}
                    onclick={() => showModal("audioStems")}
                >
                    <i class="bi bi-layers me-1"></i>{m.audio_stems_button()}
                </button>
                <button
                    type="button"
                    class="btn btn-sm btn-outline-primary text-nowrap"
                    onclick={() => showModal("mediaImport")}
                >
                    <i class="bi bi-plus-lg me-1"></i>{m.media_tab_import_button()}
                </button>
            </div>
        </div>

        <div class="media-version-list" role="list" aria-label={m.media_tab_aria_label()}>
            {#each mediaVersions as version, index (version.versionId)}
                {@const isActive = version.versionId === activeVersionId}
                {@const isSwitching = switchingVersionId === version.versionId}
                <div
                    class="media-version-row d-flex align-items-center gap-2 {isActive ? 'is-active' : ''}"
                    role="button"
                    tabindex="0"
                    aria-pressed={isActive}
                    aria-label={version.versionName}
                    onclick={() => handleSwitch(version.versionId)}
                    onkeydown={(event) => handleRowKey(event, version.versionId)}
                >
                    <span class="media-version-check text-primary">
                        {#if isSwitching}
                            <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                        {:else if isActive}
                            <i class="bi bi-check-circle-fill" title={m.media_tab_active_badge()}></i>
                        {/if}
                    </span>

                    <span class="media-version-title text-truncate flex-grow-1">
                        {version.versionName}
                    </span>

                    {#if version.storage}
                        <i
                            class="bi {isStreamingStorage(version.storage) ? 'bi-cloud' : 'bi-hdd'} text-muted"
                            title={version.storage}
                        ></i>
                    {/if}

                    <span class="media-version-duration small text-muted text-nowrap">
                        {formatDuration(version.info?.duration || version.duration)}
                    </span>

                    <span class="media-version-actions d-flex align-items-center">
                        <button
                            type="button"
                            class="btn btn-sm btn-link p-1 text-muted"
                            title={m.media_tab_move_up()}
                            aria-label={m.media_tab_move_up()}
                            disabled={index === 0}
                            onclick={(event) => { event.stopPropagation(); moveVersion(index, -1); }}
                        >
                            <i class="bi bi-arrow-up"></i>
                        </button>
                        <button
                            type="button"
                            class="btn btn-sm btn-link p-1 text-muted"
                            title={m.media_tab_move_down()}
                            aria-label={m.media_tab_move_down()}
                            disabled={index === mediaVersions.length - 1}
                            onclick={(event) => { event.stopPropagation(); moveVersion(index, 1); }}
                        >
                            <i class="bi bi-arrow-down"></i>
                        </button>
                        <button
                            type="button"
                            class="btn btn-sm btn-link p-1 text-danger"
                            title={m.media_tab_remove_button()}
                            aria-label={m.media_tab_remove_button()}
                            disabled={mediaVersions.length === 1}
                            onclick={(event) => { event.stopPropagation(); handleRemove(version.versionId); }}
                        >
                            <i class="bi bi-trash"></i>
                        </button>
                    </span>
                </div>
            {/each}
        </div>
    {/if}
</div>

<style lang="css">
.media-empty-icon {
    font-size: 2.5rem;
    display: block;
    line-height: 1;
}

.media-version-row {
    padding: 0.4rem 0.5rem;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: background-color 0.12s ease-in-out;
}

.media-version-row:hover {
    background-color: var(--bs-light);
}

.media-version-row:focus-visible {
    outline: 2px solid var(--bs-primary);
    outline-offset: -2px;
}

.media-version-row.is-active {
    background-color: var(--bs-light);
    font-weight: 500;
}

.media-version-check {
    width: 1.1rem;
    flex-shrink: 0;
    text-align: center;
    line-height: 1;
}

.media-version-title {
    min-width: 0;
}

.media-version-actions {
    flex-shrink: 0;
}

.media-version-actions .btn {
    line-height: 1;
}
</style>
